import { z } from 'zod';

// --- Publisher Configuration ---

export interface PublisherMarket {
  dmaCode: string;
  dmaName: string;
  channels: string[];
  // CTV-421 Phase 2: the US states this market's signal reaches. Single-DMA
  // stations are typically one state; border stations declare multiple
  // (e.g., KEZI/Eugene is Oregon but bleeds into Northern California, so
  // ['OR', 'CA']). Auto-filled from the DMA name parser in PublisherForm;
  // staff can edit. Surfaced as Suggested States in the wizard's geo tabs.
  // Optional so legacy `markets` JSON rows that pre-date this field still
  // parse cleanly (treated as empty when absent).
  coveredStates?: string[];
}

export interface ProductOption {
  id: string;
  name: string;
  description: string;
  cpm: number;
  subChannels: string[];
}

// CTV-518: CTVBuyer is merchant of record. The buyer pays
// budget + tax + service fee, where the service fee grosses up Stripe
// processing on the whole charge. Rates come from the stations endpoint
// (`serviceFee: { percent, fixedCents }`) so the frontend estimator can't
// drift from the backend's env-configured rates.
export interface ServiceFeeRates {
  percent: number;
  fixedCents: number;
}

// Fallback when the API payload omits serviceFee (e.g. stale cache shape).
// Matches the backend defaults: 2.9% + 30¢.
export const DEFAULT_SERVICE_FEE_RATES: ServiceFeeRates = { percent: 0.029, fixedCents: 30 };

// CTV-558: platform-wide budget floor enforced at the wizard layer (see
// ADR-0017). The "Min $X" caption and Zod refinement use
// `effectiveMinBudget(config.minBudget)` so a publisher's lower `min_budget`
// is raised to this floor in production. Dev-mode bypass below.
// Temporarily relaxed to $1 so ops can push $1 test campaigns on prod without
// a per-publisher DB edit; each publisher's own `min_budget` still applies.
export const PLATFORM_MIN_BUDGET = 1;

// Returns the effective minimum the wizard enforces. Production raises any
// sub-platform-floor publisher value to PLATFORM_MIN_BUDGET; dev-mode passes
// the publisher value through (or 0 when null) so ops can push $1 test
// campaigns without DB edits.
export const effectiveMinBudget = (publisherMinBudget: number | null | undefined, isDev: boolean): number => {
  const pub = publisherMinBudget ?? 0;
  return isDev ? pub : Math.max(PLATFORM_MIN_BUDGET, pub);
};

export interface PublisherConfig {
  slug: string;
  // Raw internal name (= stations.name). What JWT user_metadata.stations[]
  // entries match against and what the admin form's "Internal name" input
  // binds to. Consumer-facing surfaces (browse cards, headers, etc.) should
  // call displayedName(config) instead to fall back through displayName.
  name: string;
  organizationId: string | null;
  // Raw display-name column (nullable). When null, displayedName() falls
  // back to `name`. Bound directly to the admin form's "Display name" input.
  displayName?: string | null;
  logoUrl: string;
  logoInvert?: boolean; // invert logo colors to render white on dark header (default true)
  // CTV-431: header-render scale multiplier (0.5–3.0, default 1.0). Lets
  // admins enlarge small wordmark/icon logos without re-uploading the asset.
  logoScale?: number;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  fontFamily: string;
  fontUrl?: string; // Google Fonts URL to load
  coverage?: 'national' | 'regional'; // default 'regional'
  markets: PublisherMarket[];
  products: ProductOption[];
  // CTV-518: publisher-facing economics only — CTVBuyer's share of media
  // spend. Never used in buyer-facing price math (the buyer pays
  // budget + tax + service fee; rev share comes out of the publisher payout).
  revSharePercent: number;
  // Buyer-facing processing gross-up rates from the stations endpoint.
  serviceFeeRates: ServiceFeeRates;
  minBudget: number;
  minCampaignDays: number;
  // CTV-489: home-market centroid derived from markets[0]'s Nielsen DMA.
  // Drives the wizard's default radius center so the map is anchored on
  // the station before the buyer types an address.
  homeMarketCenter?: { lat: number; lng: number; label: string } | null;
  supportEmail: string;
  termsUrl: string;
}

// --- Submission row ---
// Canonical shape of a row from `selfservice_submissions`. Each page Picks
// the subset it needs; pages that previously hand-rolled a near-duplicate
// type should import this and Pick instead.

export interface SelfServiceBusinessInfo {
  businessName?: string;
  businessWebsite?: string;
  contactName?: string;
  contactPhone?: string;
}

export interface SelfServiceCampaign {
  campaignName?: string;
  budget?: number;
  startDate?: string;
  endDate?: string;
  selectedProduct?: string;
  targetMarkets?: string[];
  targetingMode?: 'default' | 'custom';
  geoTargetCenter?: { lat: number; lng: number; label: string } | null;
  geoTargetRadiusMiles?: number;
  geoTargetZips?: string[];
  clickThroughUrl?: string;
}

// Mirrors SelfserviceSubmissionStatus in selfservice-api/src/db/types.ts.
export type SelfServiceSubmissionStatus =
  | 'pending_payment'
  | 'payment_authorized'
  | 'creative_rejected'
  | 'payment_failed'
  | 'payment_refunded'
  | 'approved'
  | 'archived'
  | 'canceled';

export type SelfServicePaymentPlan = 'upfront' | 'monthly';

// CTV-462: a row in selfservice_flights identifying the Current Flight on a
// Submission for progress-bar / "where are we now" UI. See CONTEXT.md.
export interface SelfServiceCurrentFlight {
  id: string;
  installment_number: number | null;
  start_date: string;
  end_date: string;
  status: string;
  impressions: number | null;
  pacing_impressions: number | null;
}

// CTV-440: one creative row in the Live Set (status='active') for a
// submission. The submission's creatives[] is what the worker pushes to
// Publica's AdserverCreatives[]. See docs/adr/0002.
export interface SelfServiceCreativeRow {
  id: string;
  filename: string;
  storage_path: string;
  mime_type: string | null;
  size_bytes: number | null;
  name: string | null;
  // CTV-476: per-segment shares replace the legacy `weight` scalar.
  segment_shares: Array<{ start_date: string; end_date: string; share: number }>;
  click_through_url: string | null;
  // CTV-457: NOT NULL post-consolidation. Always carries an explicit window.
  creative_start_date: string;
  creative_end_date: string;
  status: 'active' | 'pending_review' | 'voided' | 'retired' | 'withdrawn' | 'abandoned';
  publica_global_mediafile_id: number | null;
  // CTV-457 Phase C: populated when the publisher rejected this creative via
  // the per-creative reject endpoint. Submission-level reject persists on the
  // submission row instead. Surfaced only on publisher-facing endpoints.
  rejection_reason: string | null;
  rejection_reason_codes: string[] | null;
  // CTV-611: set on a voided row once a recovery add (fix upload, library
  // pick, Waymark rework, or Restore) landed on its submission. Unresolved
  // rejection = status 'voided' AND resolved_at null; drives the detail-page
  // Fix & resubmit CTA and the edit-modal recovery block.
  resolved_at: string | null;
  // CTV-466 Phase 5: when this creative was rendered from a Waymark draft, we
  // surface the source draft id so the advertiser can re-open it in the
  // Waymark editor (via POST /waymark/login-for-creative). Null for raw
  // uploads from disk.
  source_draft_id: string | null;
  created_at: string;
}

// CTV-440: latest failed push job for a submission when the failure is the
// publisher-recoverable `creative_not_in_library` case. Populated only on
// publisher-facing endpoints; advertiser endpoints leave this null.
export interface SelfServicePushJobFailure {
  jobKind: 'create' | 'creative_swap' | 'edit';
  reason: 'creative_not_in_library';
  filename: string | null;
}

export interface SelfServiceMonthlyPaymentRecovery {
  installmentId: string;
  installmentNumber: number;
  amountCents: number;
  serviceFeeCents: number;
  periodStart: string;
  periodEnd: string;
  failedAt: string | null;
  nextAttemptAt: string;
  errorCode: string | null;
  errorMessage: string | null;
}

// CTV-504: per-installment row on monthly submissions. Always empty for
// upfront. Mirrors public.selfservice_payment_installments rows so the
// receipt and any charges-list UI can render the actual current schedule
// (including the bumped tail amount/period after each extension) instead
// of the frozen at-submission split implied by `amount_cents`.
export interface SelfServiceInstallment {
  id: string;
  installmentNumber: number;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  // Total charged for this installment (budget portion + its own service-fee
  // gross-up). Backend cents are authoritative — render verbatim, never
  // recompute.
  amountCents: number;
  serviceFeeCents: number;
  // CTV-518: backend rows may also carry the per-installment budget/tax
  // decomposition. Optional — tolerate absence on older payloads.
  budgetCents?: number;
  taxCents?: number;
  status: 'pending_approval' | 'scheduled' | 'processing' | 'paid' | 'failed' | 'canceled';
  paidAt: string | null;
}

export interface SelfServiceSubmissionRow {
  id: string;
  publisher_slug: string;
  contact_email: string;
  business_info: SelfServiceBusinessInfo;
  campaign: SelfServiceCampaign;
  // CTV-440: creatives are first-class rows now (see ADR-0002). `creatives`
  // is the Live Set (status='active'). CTV-611: `pendingCreativeSwap` is
  // gone; per-creative review replaced the edit-level swap review.
  creatives: SelfServiceCreativeRow[];
  // Publisher-only failure surface. Null on advertiser endpoints.
  pushJobFailure: SelfServicePushJobFailure | null;
  monthlyPaymentRecovery?: SelfServiceMonthlyPaymentRecovery | null;
  // CTV-504: live per-installment schedule. Empty array for upfront. The
  // OrderReceipt prefers this over `amount_cents` for monthly so extension
  // bumps are reflected.
  installments: SelfServiceInstallment[];
  // CTV-518 money invariant: amount_cents = total charged to the buyer =
  // budget + tax + service fee. Media spend prefers budget_cents and falls
  // back to amount_cents − service_fee_cents − tax_cents.
  amount_cents: number;
  budget_cents: number | null;
  service_fee_cents: number;
  tax_cents: number;
  // CTV-518: snapshotted rev share, present ONLY on publisher-facing rows
  // (list-by-publisher). Advertiser endpoints intentionally omit it — rev
  // share is not advertiser-visible — so the field is optional, not
  // nullable-required. Publisher share = budget × (1 − rev_share_percent).
  rev_share_percent?: number | null;
  status: SelfServiceSubmissionStatus;
  stripe_payment_intent_id: string | null;
  rejection_reason: string | null;
  rejection_reason_codes: string[] | null;
  reviewed_at: string | null;
  created_at: string;
  acknowledged_at: string | null;
  completion_acknowledged_at: string | null;
  canceled_at: string | null;
  impressions_delivered: number | null;
  pacing_impressions: number | null;
  line_item_cpm: number | null;
  impressions_updated_at: string | null;
  // CTV-462: progress-bar UI reads the Current Flight (delivering, else
  // next-upcoming, else last-completed). For upfront this is the lone flight;
  // for monthly it's the one matching the Current Flight selector. Lifetime
  // metrics (impressions_delivered, pacing_impressions) remain the SUM
  // across all flights. See ADR-0002.
  currentFlight: SelfServiceCurrentFlight | null;
  payment_plan: SelfServicePaymentPlan;
  referral_code: string | null;
  promo_code: string | null;
  // PCI-allowed card fragments populated by the Stripe webhook after the
  // buyer's PaymentIntent reaches `requires_capture`. Null until then.
  payment_method_brand: string | null;
  payment_method_last4: string | null;
}

// --- Form Data ---

// CTV-457: per-Creative slot in the wizard's Step 3. The wizard accumulates
// SlotState[] in form data; submit serializes each non-empty slot as a row in
// selfservice_creatives. The Scheduler (rendered when length >= 2) edits the
// dates and weights here. File refs and in-flight upload promises live
// alongside the form in per-slot Maps on the wizard component — they can't be
// serialized into RHF.
export interface CreativeSlotState {
  // Stable client-side identifier. Used to key per-slot Maps (Files, upload
  // promises) on the wizard and to remember which slot a Waymark render
  // belongs to. Never sent to the server.
  slotId: string;
  // Populated by the eager-upload path once Supabase Storage has the bytes.
  // Null between file pick and upload completion.
  storagePath: string | null;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  clickThroughUrl: string;
  // CTV-476: per-segment shares. Together they partition [startDate, endDate]
  // with no gaps. Cross-creative Rotation Segments are derived at render time
  // by unioning sibling boundaries; this array is the source of truth for
  // each individual creative's shares.
  segmentShares: Array<{ startDate: string; endDate: string; share: number }>;
  // YYYY-MM-DD strings. Default to the submission window when a slot is
  // first added; the Scheduler clamps within that window.
  startDate: string;
  endDate: string;
  // CTV-466 follow-up: when an advertiser opens a creative_rejected campaign
  // via Fix & Resubmit, voided creatives are carried back into the wizard so
  // the user can see exactly what was rejected. These three fields are
  // populated only on those carried-forward rejection rows; the wizard's
  // commercial step renders them as a red header band + reviewer notes, and
  // hard-gates Next until every rejected slot is either replaced or removed.
  rejected?: boolean;
  rejectionReason?: string | null;
  rejectionReasonCodes?: string[] | null;
  // The original selfservice_creatives.id this slot was hydrated from, so the
  // backend resubmit endpoint can recognize and replace the row in place.
  sourceCreativeId?: string;
}

export interface SelfServiceFormData {
  // Step 1: Business Info
  businessName: string;
  businessWebsite: string;
  contactEmail: string;
  contactName: string;
  contactPhone: string;

  // Step 2: Campaign Setup
  campaignName: string;
  budget: number | null;
  selectedProduct: string;
  startDate: string;
  endDate: string;
  targetMarkets: string[];
  targetingMode?: 'default' | 'custom';
  // CTV-421: custom-targeting tab the user is on. Each tab holds its own
  // selection list — switching tabs does not wipe the others, so the wizard
  // can serialize whichever tab is active on submit.
  // CTV-502: the explicit-ZIPs tab was removed; ZIP targeting now goes
  // through the Radius tool only.
  geoMode?: 'states' | 'dmas' | 'radius';
  selectedStates?: string[];
  selectedDMAs?: string[];
  geoTargetCenter?: { lat: number; lng: number; label: string } | null;
  geoTargetRadiusMiles?: number;
  // Computed once per geocode by the fake-coverage calc in RadiusGeoTargeting.
  // Persisted on the form (not local component state) so leaving and
  // returning to the Radius tab doesn't replay the spinner and clobber
  // the user's slider value. Cleared when the user changes the address.
  geoTargetComputedMinRadius?: number;
  geoTargetZips?: string[];

  // Step 3: Creative
  // Source of truth for the wizard's multi-creative state. Slot 0 lives at
  // index 0; the scheduler reads from this same array.
  creatives: CreativeSlotState[];

  // Step 4: Review
  acceptTerms: boolean;
  acceptRefundPolicy: boolean;
  paymentPlan: 'upfront' | 'monthly' | null;

  // Referral / promo codes — captured from ?ref= / ?promo= URL params or
  // entered manually on the review step. Stored as the user-typed value;
  // normalized + revalidated server-side at submit.
  referralCode: string;
  promoCode: string;
}

// --- Zod Schemas (per-step) ---

// Accepts "example.com", "sub.example.co.uk", "https://x.io/path", etc.
// Rejects "asdf", "test.", "@@@". Protocol is optional — bare hostnames are
// the common case for business website inputs.
const websitePattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[^\s]*)?$/i;

// Matches the format produced by `formatUSPhone` in BusinessInfoStep:
// "(XXX) XXX-XXXX". We don't accept other shapes because the input formats
// digits as the user types — anything that doesn't match this shape is a
// partial / invalid entry.
const phonePattern = /^\(\d{3}\) \d{3}-\d{4}$/;

export const businessInfoSchema = z.object({
  businessName: z.string().min(1, 'Business name is required'),
  businessWebsite: z
    .string()
    .min(1, 'Business website is required')
    .regex(websitePattern, 'Enter a valid website (e.g. example.com)'),
  contactEmail: z.string().min(1, 'Contact email is required').email('Enter a valid email'),
  contactName: z.string().optional().default(''),
  contactPhone: z
    .string()
    .optional()
    .default('')
    .refine((v) => !v || phonePattern.test(v), 'Enter a valid 10-digit US phone number'),
});

// Base campaign fields (without refinements) — used for building the combined schema.
// Geo fields are declared here so the full wizard schema (buildFullSchema) accepts
// them on the RHF form; superRefine in campaignSetupSchema is what actually
// enforces the cross-field empty-custom rule.
export const campaignSetupBaseSchema = (minBudget: number) =>
  z.object({
    campaignName: z.string().optional().default(''),
    budget: z.number({ message: 'Budget is required' })
      .min(minBudget, `Minimum budget is $${minBudget.toLocaleString()}`),
    selectedProduct: z.string().min(1, 'Select a product'),
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
    // Optional: an empty list means "no geo restriction beyond the publisher's
    // channels" (`targetingMode='default'`). Multi-market publishers may pick
    // a subset; leaving it blank runs across every market the publisher owns.
    // Custom-mode narrowing (states / DMAs / radius) is enforced separately
    // in `applyCampaignCrossFieldRules`.
    targetMarkets: z.array(z.string()).optional().default([]),
    targetingMode: z.enum(['default', 'custom']).optional(),
    geoMode: z.enum(['states', 'dmas', 'radius']).optional(),
    selectedStates: z.array(z.string()).optional(),
    selectedDMAs: z.array(z.string()).optional(),
    geoTargetZips: z.array(z.string()).optional(),
  });

// Shared cross-field refinement applied to BOTH the step-2 standalone schema
// (campaignSetupSchema) AND the wizard's full-form resolver (buildFullSchema in
// SelfServiceWizard.tsx). Living here keeps the two in lockstep so a rule
// added in one place can't drift from the other.
type CampaignCrossFieldData = {
  startDate?: string;
  endDate?: string;
  targetingMode?: 'default' | 'custom';
  selectedStates?: string[];
  selectedDMAs?: string[];
  geoTargetZips?: string[];
};

export function applyCampaignCrossFieldRules(
  data: CampaignCrossFieldData,
  ctx: z.RefinementCtx,
  minDays: number,
): void {
  if (data.startDate && data.endDate) {
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    if (!(end > start)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'End date must be after start date',
      });
    } else {
      const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays < minDays) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['endDate'],
          message: `Campaign must run for at least ${minDays} days`,
        });
      }
    }
  }
  if (data.targetingMode === 'custom') {
    const anyGeo =
      (data.selectedStates?.length ?? 0) > 0 ||
      (data.selectedDMAs?.length ?? 0) > 0 ||
      (data.geoTargetZips?.length ?? 0) > 0;
    if (!anyGeo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetingMode'],
        message:
          'Pick at least one state, DMA, or radius location — or select the default option above.',
      });
    }
  }
}

// Full campaign schema with cross-field refinements — used for standalone
// step-2 validation testing. The wizard's actual runtime resolver in
// SelfServiceWizard.tsx applies the same `applyCampaignCrossFieldRules` to
// its full-form schema so trigger() and onChange re-validation both see
// every rule.
//
// CTV-489: the per-station max-campaign-days ceiling has been removed
// entirely (column + form input + wizard refinement). The wizard's
// inventory-feasibility check is the effective cap on multi-year runs.
export const campaignSetupSchema = (minBudget: number, minDays: number) => {
  return campaignSetupBaseSchema(minBudget).superRefine((data, ctx) => {
    applyCampaignCrossFieldRules(data, ctx, minDays);
  });
};

// Accepts "example.com" or "www.example.com" by auto-prepending http:// before
// validating; users shouldn't have to type a protocol. We prepend http (not
// https) so that HTTP-only sites still resolve — HTTPS sites auto-redirect
// from http to https anyway.
export const lenientUrl = z
  .string()
  .optional()
  .default('')
  .transform((v) => (v && !/^https?:\/\//i.test(v) ? `http://${v}` : v))
  .refine((v) => v === '' || /^https?:\/\/[^\s.]+\.[^\s]+$/i.test(v), {
    message: 'Enter a valid URL (e.g. www.example.com)',
  });

// Creatives are validated at runtime by the wizard's step-3 advance gate
// (slot 0 has a storagePath, every slot's click-through URL parses cleanly).
// No Zod schema needed for step 3 — the per-slot state lives outside RHF.
export const creativeSchema = z.object({});

export const reviewSchema = z.object({
  acceptTerms: z.literal(true, { message: 'You must accept the terms and conditions' }),
  acceptRefundPolicy: z.literal(true, { message: 'You must acknowledge the refund policy' }),
  paymentPlan: z.enum(['upfront', 'monthly'], { message: 'Choose a payment plan' }),
  referralCode: z.string().optional().default(''),
  promoCode: z.string().optional().default(''),
});

// --- Default Form Values ---

export const defaultFormValues: SelfServiceFormData = {
  businessName: '',
  businessWebsite: '',
  contactEmail: '',
  contactName: '',
  contactPhone: '',
  campaignName: '',
  budget: null,
  selectedProduct: '',
  startDate: '',
  endDate: '',
  targetMarkets: [],
  targetingMode: 'default',
  // Matches GeoTargetingTabs' visible default — without this, a buyer who
  // toggles to custom mode and uses the already-showing Radius tab without
  // clicking any tab button submits with geoMode=undefined, and the API
  // serializer silently falls back to the publisher's default markets.
  geoMode: 'radius',
  geoTargetCenter: null,
  geoTargetRadiusMiles: 25,
  geoTargetZips: [],
  creatives: [],
  acceptTerms: false,
  acceptRefundPolicy: false,
  paymentPlan: null,
  referralCode: '',
  promoCode: '',
};

// --- Helpers ---

export function calculateImpressions(budget: number, cpm: number): number {
  if (!budget || !cpm || cpm <= 0) return 0;
  return Math.floor((budget / cpm) * 1000);
}

// CTV-518: service-fee gross-up. Total = ceil((budget + tax + fixed) / (1 −
// percent)) so the fee never under-covers Stripe's processing cost; the
// service fee is whatever's left after budget + tax. MUST mirror
// selfservice-api/src/shared/payments-math.ts to the cent — the backend's
// charge amounts are authoritative and the UI estimate has to agree.
// Tax is always 0 today; the param is the seam for enabling sales tax later.
export function estimateTotalWithServiceFeeCents(
  budgetCents: number,
  rates: ServiceFeeRates,
  taxCents = 0,
): number {
  if (!isFinite(budgetCents) || budgetCents <= 0) return 0;
  return Math.ceil((budgetCents + taxCents + rates.fixedCents) / (1 - rates.percent));
}

export function estimateServiceFeeCents(
  budgetCents: number,
  rates: ServiceFeeRates,
  taxCents = 0,
): number {
  if (!isFinite(budgetCents) || budgetCents <= 0) return 0;
  return estimateTotalWithServiceFeeCents(budgetCents, rates, taxCents) - budgetCents - taxCents;
}

// Dollar-level conveniences for components that work in dollars (budget
// inputs, calculator display). Round-trips through cents so the displayed
// numbers always match the backend's cent math.
export function estimateServiceFee(budget: number, rates: ServiceFeeRates, tax = 0): number {
  return estimateServiceFeeCents(Math.round(budget * 100), rates, Math.round(tax * 100)) / 100;
}

export function estimateTotalWithServiceFee(budget: number, rates: ServiceFeeRates, tax = 0): number {
  return estimateTotalWithServiceFeeCents(Math.round(budget * 100), rates, Math.round(tax * 100)) / 100;
}

// Number of calendar months a campaign window touches.
// Jan 5 → Jan 31 = 1; Jan 5 → Feb 5 = 2; Jan 5 → Mar 31 = 3.
export function calendarMonthsSpanned(startDate: string, endDate: string): number {
  if (!startDate || !endDate) return 0;
  const s = new Date(startDate.slice(0, 10) + 'T00:00:00');
  const e = new Date(endDate.slice(0, 10) + 'T00:00:00');
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return 0;
  return (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1;
}

// Stub discount tiers — tune once we have data. Applied as % off total.
export function prepayDiscountPercent(months: number): number {
  if (months >= 4) return 0.10;
  if (months === 3) return 0.08;
  if (months === 2) return 0.05;
  return 0;
}

// Anniversary-day billing schedule. Each period runs from the campaign's
// start day-of-month to the same day next month (e.g. May 10 → Jun 9). The
// BUDGET is split across periods: full anniversary months get the same flat
// monthly portion; the trailing partial month (if any) is prorated against
// what its full period would have been (e.g. Jul 10 – Jul 31 = 22/31 of the
// rate). Day-31 starts clamp to the last day of shorter months.
//
// CTV-518: each installment is its own card charge, so each entry's `amount`
// is that period's budget portion PLUS its own service-fee gross-up (the
// per-charge fixed cents apply every month). The sum of entry amounts
// therefore legitimately exceeds the single upfront gross-up total — that's
// the contract, not a bug. Used by both the review-step UI and the wizard's
// today-charge calculation — keep them aligned through this helper.
export interface ChargeScheduleEntry {
  label: string;
  amount: number;
  coversLabel: string;
  days: number;
  isPartial: boolean;
}

export function buildChargeSchedule(
  startDateStr: string | null | undefined,
  endDateStr: string | null | undefined,
  budget: number,
  rates: ServiceFeeRates,
): ChargeScheduleEntry[] {
  if (!startDateStr || !endDateStr) return [];
  // Coerce to a "YYYY-MM-DD" prefix so callers that accidentally pass a
  // non-string (e.g. a pg Date object) don't produce an Invalid Date and
  // turn the while-loop below into an infinite NaN-comparison loop.
  const startPrefix = String(startDateStr).slice(0, 10);
  const endPrefix = String(endDateStr).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startPrefix) || !/^\d{4}-\d{2}-\d{2}$/.test(endPrefix)) return [];
  const start = parseDateOnlyUtc(startPrefix);
  const end = parseDateOnlyUtc(endPrefix);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  const totalDays = Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
  // Bail on backwards dates, NaN/Infinity budgets, and zero/negative budgets.
  // Producing a schedule of negative or $0 charges is misleading; the
  // wizard should not be calling us in those cases anyway.
  if (totalDays <= 0 || !isFinite(budget) || budget <= 0) return [];

  const fmtLong = (d: Date) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const fmtShort = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

  const anniversary = (n: number): Date => {
    const totalMonths = start.getUTCMonth() + n;
    const year = start.getUTCFullYear() + Math.floor(totalMonths / 12);
    const month = ((totalMonths % 12) + 12) % 12;
    const lastDayOfTargetMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    return new Date(Date.UTC(year, month, Math.min(start.getUTCDate(), lastDayOfTargetMonth)));
  };

  type Raw = { periodStart: Date; periodEnd: Date; naturalDays: number; actualDays: number };
  const raw: Raw[] = [];
  let n = 0;
  // Hard cap at 240 (20 years) so any future date-math regression that
  // produces an Invalid Date can't lock the browser tab.
  while (n < 240) {
    const periodStart = anniversary(n);
    if (isNaN(periodStart.getTime()) || periodStart > end) break;
    const nextAnniv = anniversary(n + 1);
    if (isNaN(nextAnniv.getTime())) break;
    const naturalEnd = new Date(nextAnniv.getTime() - 86400000);
    const periodEnd = naturalEnd > end ? end : naturalEnd;
    const naturalDays = Math.floor((naturalEnd.getTime() - periodStart.getTime()) / 86400000) + 1;
    const actualDays = Math.floor((periodEnd.getTime() - periodStart.getTime()) / 86400000) + 1;
    raw.push({ periodStart, periodEnd, naturalDays, actualDays });
    n += 1;
  }

  const lastIdx = raw.length - 1;
  const lastIsPartial = lastIdx >= 0 && raw[lastIdx].actualDays < raw[lastIdx].naturalDays;
  const fullCount = lastIsPartial ? lastIdx : raw.length;
  const partialFraction = lastIsPartial ? raw[lastIdx].actualDays / raw[lastIdx].naturalDays : 0;
  const denominator = fullCount + partialFraction;
  const budgetCents = Math.round(budget * 100);
  const monthlyRateCents = denominator > 0 ? budgetCents / denominator : 0;

  // Split the BUDGET into per-period portions first (penny remainder lands
  // on the last portion so the portions sum exactly to the budget), then
  // gross up each portion independently — every installment is its own
  // charge with its own service fee.
  const budgetPortionsCents: number[] = raw.map((_, i) => {
    const isPartial = lastIsPartial && i === lastIdx;
    return Math.round(isPartial ? monthlyRateCents * partialFraction : monthlyRateCents);
  });
  if (budgetPortionsCents.length > 0) {
    const sumCents = budgetPortionsCents.reduce((s, c) => s + c, 0);
    const diffCents = budgetCents - sumCents;
    if (diffCents !== 0) budgetPortionsCents[budgetPortionsCents.length - 1] += diffCents;
  }

  return raw.map((p, i) => ({
    label: fmtLong(p.periodStart),
    amount: estimateTotalWithServiceFeeCents(budgetPortionsCents[i], rates) / 100,
    coversLabel: `${fmtShort(p.periodStart)} – ${fmtShort(p.periodEnd)}`,
    days: p.actualDays,
    isPartial: lastIsPartial && i === lastIdx,
  }));
}

// Sum of a charge schedule's per-installment amounts, in dollars, rounded at
// the cent so it equals what the buyer's card is actually charged across all
// installments. This is the authoritative "total over N months" figure —
// higher than the single upfront gross-up because each installment carries its
// own service-fee fixed cents. (CTV-575)
export function chargeScheduleTotal(entries: ChargeScheduleEntry[]): number {
  return entries.reduce((s, e) => s + Math.round(e.amount * 100), 0) / 100;
}

// CTV-504: convert live `selfservice_payment_installments` rows into the
// same ChargeScheduleEntry shape buildChargeSchedule produces. Used by the
// receipt for monthly campaigns so post-extension state (bumped tail
// amount/period, inserted new flights) renders correctly. A partial entry
// is one whose period is shorter than the natural month anchored to its
// start date — same rule the wizard's schedule uses.
export function installmentsToScheduleEntries(
  installments: SelfServiceInstallment[],
): ChargeScheduleEntry[] {
  if (!installments.length) return [];
  const fmtLong = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const fmtShort = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const sorted = [...installments].sort((a, b) => a.installmentNumber - b.installmentNumber);
  return sorted.map((inst) => {
    const start = parseDateOnlyUtc(inst.periodStart);
    const end = parseDateOnlyUtc(inst.periodEnd);
    const validRange = !isNaN(start.getTime()) && !isNaN(end.getTime());
    const days = validRange
      ? Math.floor((end.getTime() - start.getTime()) / 86400000) + 1
      : 0;
    // Natural bucket length is the gap to next anniversary anchored to start
    // day-of-month, with day-31 clamp; mirrors buildChargeSchedule.
    let naturalDays = days;
    if (validRange) {
      const startMonths = start.getUTCMonth() + 1;
      const yr = start.getUTCFullYear() + Math.floor(startMonths / 12);
      const mo = ((startMonths % 12) + 12) % 12;
      const lastOfNext = new Date(Date.UTC(yr, mo + 1, 0)).getUTCDate();
      const nextAnniv = new Date(
        Date.UTC(yr, mo, Math.min(start.getUTCDate(), lastOfNext)),
      );
      naturalDays = Math.floor((nextAnniv.getTime() - start.getTime()) / 86400000);
    }
    return {
      label: validRange ? fmtLong(start) : inst.periodStart,
      amount: inst.amountCents / 100,
      coversLabel: validRange ? `${fmtShort(start)} – ${fmtShort(end)}` : '',
      days,
      isPartial: validRange && days < naturalDays,
    };
  });
}

function parseDateOnlyUtc(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return new Date(NaN);
  }
  return date;
}

// --- Referrals & Promotions ---

export interface SelfServiceSalesperson {
  id: string;
  publisher_slug: string;
  name: string;
  email: string | null;
  code: string;
  default_discount_percent: number | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SelfServicePromotion {
  id: string;
  publisher_slug: string;
  code: string;
  discount_percent: number;
  starts_at: string | null;
  ends_at: string | null;
  max_uses: number | null;
  current_uses: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

// Default 6-char referral code from a name: first 3 of first name + first 3
// of last name. Pads with unambiguous alphanumerics when the name is short
// (e.g. "Bo Wu" → "BOWU" + 2 random).
export function suggestReferralCode(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = (parts[0] ?? '').replace(/[^A-Za-z]/g, '').toUpperCase();
  const last = (parts.slice(1).join('') ?? '').replace(/[^A-Za-z]/g, '').toUpperCase();
  let head = first.slice(0, 3) + last.slice(0, 3);
  // Drop ambiguous I/O/0/1 from the random pool to avoid "is it a 1 or an I?"
  const pool = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  while (head.length < 6) {
    head += pool[Math.floor(Math.random() * pool.length)];
  }
  return head.slice(0, 6);
}

// Promo codes are publisher-chosen; we only normalize them.
export function normalizePromoCode(s: string): string {
  return s.trim().toUpperCase();
}
